import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
type DrawingMode = 'Draw Polygon' | 'Draw Line' | string;

type Props = {
    mode: DrawingMode;
    polygonDraftVertexCount?: number;
    lineDraftVertexCount?: number;
};

const DrawingInstructions = ({
    mode,
    polygonDraftVertexCount = 0,
    lineDraftVertexCount = 0,
}: Props) => {
    const { t } = useTranslation('map');
    const polygonMessage = t('drawingInstructions.polygon');
    const lineMessage = t('drawingInstructions.line');
    const [dismissed, setDismissed] = useState(false);
    const previousPolygonVertexCount = useRef(polygonDraftVertexCount);
    const previousLineVertexCount = useRef(lineDraftVertexCount);

    // Show instructions again when user switches drawing mode
    useEffect(() => {
        setDismissed(false);
    }, [mode]);

    useEffect(() => {
        const polygonJustCompleted =
            mode === 'Draw Polygon' &&
            previousPolygonVertexCount.current >= 3 &&
            polygonDraftVertexCount === 0;
        const lineJustCompleted =
            mode === 'Draw Line' &&
            previousLineVertexCount.current >= 2 &&
            lineDraftVertexCount === 0;

        if (polygonJustCompleted || lineJustCompleted) {
            setDismissed(true);
        }

        previousPolygonVertexCount.current = polygonDraftVertexCount;
        previousLineVertexCount.current = lineDraftVertexCount;
    }, [mode, polygonDraftVertexCount, lineDraftVertexCount]);

    if (dismissed) return null;

    const message = mode === 'Draw Polygon' ? polygonMessage : mode === 'Draw Line' ? lineMessage : null;
    if (!message) return null;

    return (
        <Box
            sx={{
                position: 'absolute',
                // Anchor near map top while respecting device safe-area insets.
                top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
                left: '50%',
                transform: 'translateX(-50%)',
                // Float above drawers/sidebars so the drawing guidance stays visible.
                zIndex: 2305,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                maxWidth: 'min(520px, calc(100vw - 24px))',
                padding: '12px 12px 12px 14px',
                backgroundColor: 'surface.elevated',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'surface.borderSubtle',
                boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
                <Typography
                    variant="body2"
                    sx={{ display: 'block', color: 'actionBrand.primary', fontWeight: 800, letterSpacing: 0.2, mb: 0.25 }}
                >
                    {t('drawingInstructions.drawingMode')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, lineHeight: 1.35 }}>
                    {message}
                </Typography>
            </Box>
            <IconButton
                size="small"
                onClick={() => setDismissed(true)}
                aria-label={t('drawingInstructions.dismiss')}
                sx={{
                    margin: -0.5,
                    color: 'text.secondary',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        color: 'text.primary',
                    },
                }}
            >
                <Icon size={18} name="xmark" />
            </IconButton>
        </Box>
    );
};

export default DrawingInstructions;
